import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { isDialogOpen, isTextEntryElement } from '~/utils/textEntry';

export type UndoRedoShortcut = 'undo' | 'redo';

export interface ContourUndoRedoShortcutHandlers {
    /** The shortcuts do nothing at all while this is false. */
    enabled: boolean;
    canUndo: boolean;
    canRedo: boolean;
    undo: () => void;
    redo: () => void;
}

/**
 * Pure: which action, if any, a key event asks for. Undo is Ctrl+Z, redo is
 * Ctrl+Y, on every platform.
 *
 * Deliberately Ctrl-only, including on macOS. The Cmd pair cannot be completed
 * there — Cmd+Y is History in Safari — and a set where Cmd+Z undoes but its
 * counterpart does nothing is worse than no Cmd binding at all: the user finds
 * half a convention and has to discover that the other half is somewhere else.
 * Ctrl+Z and Ctrl+Y both work on macOS browsers, so one pair covers everyone
 * and one line of documentation describes it.
 *
 * Every other modifier is rejected. Alt in particular: Ctrl+Alt is AltGr on
 * several keyboard layouts, where Ctrl+Alt+Z is a character the user meant to
 * type, not an undo.
 */
export function shortcutFor(event: KeyboardEvent): UndoRedoShortcut | null {
    if (!event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
        return null;
    }
    const key = event.key.toLowerCase();
    if (key === 'z') {
        return 'undo';
    }
    if (key === 'y') {
        return 'redo';
    }
    return null;
}

/**
 * Pure: the reason, if any, that the app must not claim this key event.
 * Split out from the hook so every refusal is verifiable without mounting it.
 */
export function shortcutRefusal(
    event: KeyboardEvent,
    activeElement: Element | null,
    hasOpenDialog: boolean,
    isToolActionInProgress: boolean
): string | null {
    // Ctrl+Z inside a field is the browser's own undo, and a rename or a
    // description box is exactly where a user reaches for it. Taking it would
    // both lose their typing and silently revert a contour behind the field.
    if (isTextEntryElement(activeElement)) {
        return 'text-input';
    }
    // A modal has the user's attention and the contour it would revert is
    // behind it, unseen. Whatever they are confirming, they are not asking to
    // undo the edit underneath.
    if (hasOpenDialog) {
        return 'dialog-open';
    }
    // Never revert out from under a half-drawn line or a held brush: the
    // stroke would finish onto a contour that is no longer the one it started
    // from.
    if (isToolActionInProgress) {
        return 'tool-action-in-progress';
    }
    // A held key would walk the stack faster than the viewport redraws, so the
    // user could not see what came off. One press, one step.
    if (event.repeat) {
        return 'auto-repeat';
    }
    return null;
}

/**
 * Binds Ctrl+Z and Ctrl+Y to the selected structure's undo and redo.
 *
 * Mounted by the toolbar's undo/redo control, so it is live exactly while
 * those buttons are on screen and shares their gating — there is no second
 * definition of when an undo is allowed, and no listener outstanding in the
 * registration or planning steps, where Ctrl+Z would otherwise revert a
 * contour the user is not even looking at.
 *
 * The event is claimed (preventDefault) only when an action actually runs, so
 * every refusal above leaves the key to whatever else would handle it.
 */
export function useContourUndoRedoShortcuts(
    handlers: ContourUndoRedoShortcutHandlers
): void {
    // The handlers are rebuilt every render; a ref keeps the listener stable
    // instead of resubscribing the window on each one.
    const latest = useRef(handlers);
    useEffect(() => {
        latest.current = handlers;
    });
    const toolManager =
        useRecoilValue(viewManagerState)?.interactionContext.toolManager;

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            const { enabled, canUndo, canRedo, undo, redo } = latest.current;
            if (!enabled) {
                return;
            }
            const action = shortcutFor(event);
            if (!action) {
                return;
            }
            const refusal = shortcutRefusal(
                event,
                document.activeElement,
                isDialogOpen(),
                toolManager?.isActiveToolActionInProgress() ?? false
            );
            if (refusal) {
                return;
            }
            if (action === 'undo' && canUndo) {
                event.preventDefault();
                undo();
            } else if (action === 'redo' && canRedo) {
                event.preventDefault();
                redo();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [toolManager]);
}
